import React from 'react';

import { Button as PrismUIButton } from '@nutanix-ui/prism-reactjs';
import type { ButtonProps as PrismUIButtonProps } from '@nutanix-ui/prism-reactjs/lib/components/v2/Button/Button';
import { ButtonTypes, TextButtonSizes } from '@nutanix-ui/prism-reactjs/lib/components/v2/Button/Button';
import classnames from 'classnames';

import Loader, { LoaderType } from '../Loader/Loader';

import './Button.scss';

export { ButtonTypes, TextButtonSizes };

export type ButtonProps = PrismUIButtonProps & {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  className?: string;
  loading?: boolean;
};

const buttonLoaderTypes = {
  [ButtonTypes.PRIMARY]: LoaderType.WHITE,
  [ButtonTypes.SECONDARY]: LoaderType.GRAY,
  [ButtonTypes.DESTRUCTIVE]: LoaderType.WHITE,
  [ButtonTypes.SUCCESS]: LoaderType.WHITE,
  [ButtonTypes.WARNING]: LoaderType.WHITE,
  [ButtonTypes.BORDERLESS]: LoaderType.GRAY,
  [ButtonTypes.NAVIGATION_PRIMARY]: LoaderType.WHITE,
  [ButtonTypes.NAVIGATION_SECONDARY]: LoaderType.WHITE,
  [ButtonTypes.NAVIGATION_DESTRUCTIVE]: LoaderType.WHITE,
  [ButtonTypes.NAVIGATION_BORDERLESS]: LoaderType.WHITE,
  [ButtonTypes.ICON_DEFAULT]: LoaderType.GRAY,
  [ButtonTypes.ICON_SECONDARY]: LoaderType.GRAY,
  [ButtonTypes.ICON_LINK]: LoaderType.DEFAULT,
  [ButtonTypes.ICON_NAVIGATION]: LoaderType.GRAY,
  [ButtonTypes.TEXT_NORMAL]: LoaderType.DEFAULT,
  [ButtonTypes.TEXT_PRIMARY]: LoaderType.GRAY,
  [ButtonTypes.TEXT_SECONDARY]: LoaderType.GRAY,
  [ButtonTypes.TEXT_DESTRUCTIVE]: LoaderType.DEFAULT,
  [ButtonTypes.TEXT_ALERT_INFO]: LoaderType.DEFAULT,
  [ButtonTypes.TEXT_ALERT_SUCCESS]: LoaderType.DEFAULT,
  [ButtonTypes.TEXT_ALERT_WARNING]: LoaderType.DEFAULT,
  [ButtonTypes.TEXT_ALERT_ERROR]: LoaderType.DEFAULT,
};

export type ButtonAppearance = 'default' | 'square' | 'mini' | 'transparent-mini' | 'underline';

export default function Button({
  type = ButtonTypes.PRIMARY,
  className,
  leftIcon,
  rightIcon,
  children,
  loading,
  ...props
}: React.PropsWithChildren<ButtonProps>) {
  const classNames = classnames(className, 'button', `button--${type}`, {
    'button--loading': loading,
  });

  return (
    <PrismUIButton {...props} key={props.id} type={type} className={classNames}>
      {loading && <Loader type={buttonLoaderTypes[type]} className="button__loader" data-test="button-loader" />}

      {leftIcon && <span className="button__icon-left">{leftIcon}</span>}

      {children && <span className="button__content">{children}</span>}

      {rightIcon && <span className="button__icon-right">{rightIcon}</span>}
    </PrismUIButton>
  );
}
